import { useState, useEffect } from 'react';
import { getActivityLog, type ActivityLogEntry } from '../api';
import type { ChatbotListItem } from '@neural-intelligence/shared';

const ACTION_LABELS: Record<string, string> = {
  'chatbot.create': 'Created chatbot',
  'chatbot.update': 'Updated chatbot',
  'chatbot.delete': 'Deleted chatbot',
  'file.upload': 'Uploaded file',
  'file.delete': 'Deleted file',
  'faq.create': 'Created FAQ',
  'faq.update': 'Updated FAQ',
  'faq.delete': 'Deleted FAQ',
  'domain.add': 'Added domain',
  'domain.remove': 'Removed domain',
  'email.connect': 'Connected email',
  'email.disconnect': 'Disconnected email',
  'email_config.update': 'Updated email config',
  'training_email.add': 'Added training email',
  'training_email.delete': 'Deleted training email',
  'training_email.upload': 'Uploaded training emails',
  'report_email.add': 'Added report email',
  'report_email.remove': 'Removed report email',
  'user.create': 'Created user',
  'user.delete': 'Deleted user',
  'user.password_reset': 'Reset password',
  'customer.create': 'Created customer',
  'customer.delete': 'Deleted customer',
  'settings.retention_update': 'Updated retention settings',
  'retention.cleanup': 'Data cleanup',
};

function formatAction(action: string): string {
  return ACTION_LABELS[action] || action.replace(/[._]/g, ' ');
}

function formatDetails(details: string | null): string {
  if (!details) return '';
  try {
    const obj = JSON.parse(details);
    return Object.entries(obj)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
      .join(', ');
  } catch {
    return details;
  }
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr + 'Z').getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr + 'Z').toLocaleDateString();
}

export default function ActivityLogSection({ chatbots }: { chatbots: ChatbotListItem[] }) {
  const [entries, setEntries] = useState<ActivityLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [filterChatbot, setFilterChatbot] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [debouncedUser, setDebouncedUser] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedUser(filterUser), 300);
    return () => clearTimeout(timer);
  }, [filterUser]);

  useEffect(() => {
    loadEntries();
  }, [filterChatbot, filterAction, debouncedUser, offset]);

  async function loadEntries() {
    setLoading(true);
    const result = await getActivityLog({
      chatbot_id: filterChatbot || undefined,
      action: filterAction || undefined,
      user_email: debouncedUser || undefined,
      limit,
      offset,
    });
    if (result.success) {
      setEntries(result.data || []);
      setTotal(result.total || 0);
    }
    setLoading(false);
  }

  return (
    <div style={{
      background: 'var(--color-bg-white)',
      border: '1px solid var(--color-border)',
      borderRadius: '12px',
      padding: '24px',
      marginTop: '24px'
    }}>
      <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px', color: 'var(--color-text-primary)' }}>
        Activity Log
      </h2>
      <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '20px' }}>
        View a history of all actions performed across the platform.
      </p>

      <div className="filter-bar" style={{ marginBottom: '20px' }}>
        <select
          value={filterChatbot}
          onChange={e => { setFilterChatbot(e.target.value); setOffset(0); }}
          className="input"
        >
          <option value="">All chatbots</option>
          {chatbots.map(cb => (
            <option key={cb.id} value={cb.id}>{cb.name}</option>
          ))}
        </select>
        <select
          value={filterAction}
          onChange={e => { setFilterAction(e.target.value); setOffset(0); }}
          className="input"
        >
          <option value="">All actions</option>
          {Object.keys(ACTION_LABELS).map(a => (
            <option key={a} value={a}>{ACTION_LABELS[a]}</option>
          ))}
        </select>
        <input
          type="text"
          value={filterUser}
          onChange={e => { setFilterUser(e.target.value); setOffset(0); }}
          className="input"
          placeholder="Filter by user email..."
        />
      </div>

      <div className="table-responsive" style={{ padding: 0, border: '1px solid var(--color-border)', borderRadius: '8px', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}><div className="spinner" /></div>
        ) : entries.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)' }}>No activity logged yet</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg-secondary)', borderBottom: '1px solid var(--color-border)' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--color-text-secondary)' }}>When</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--color-text-secondary)' }}>User</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--color-text-secondary)' }}>Action</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--color-text-secondary)' }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr key={entry.id} style={{ borderBottom: '1px solid var(--color-border-light)' }}>
                  <td style={{ padding: '10px 16px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                    <span title={new Date(entry.created_at + 'Z').toLocaleString()}>{timeAgo(entry.created_at)}</span>
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    <span style={{ fontWeight: 500 }}>{entry.user_email}</span>
                    {entry.user_role && (
                      <span style={{
                        marginLeft: '6px',
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '1px 6px',
                        borderRadius: '4px',
                        background: entry.user_role === 'admin' ? 'var(--color-role-admin-bg)' : entry.user_role === 'customer' ? 'var(--color-role-customer-bg)' : 'var(--color-role-sales-bg)',
                        color: entry.user_role === 'admin' ? 'var(--color-role-admin-text)' : entry.user_role === 'customer' ? 'var(--color-role-customer-text)' : 'var(--color-role-sales-text)',
                      }}>{entry.user_role}</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 600,
                      background: entry.action.includes('delete') || entry.action.includes('remove') || entry.action.includes('disconnect')
                        ? 'var(--color-danger-light)' : entry.action.includes('create') || entry.action.includes('add') || entry.action.includes('connect')
                        ? 'var(--color-success-light)' : 'var(--color-primary-light)',
                      color: entry.action.includes('delete') || entry.action.includes('remove') || entry.action.includes('disconnect')
                        ? 'var(--color-danger)' : entry.action.includes('create') || entry.action.includes('add') || entry.action.includes('connect')
                        ? 'var(--color-success)' : 'var(--color-primary)',
                    }}>
                      {formatAction(entry.action)}
                    </span>
                  </td>
                  <td style={{ padding: '10px 16px', color: 'var(--color-text-secondary)', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {formatDetails(entry.details)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > limit && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
          <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
            Showing {offset + 1}-{Math.min(offset + limit, total)} of {total}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn btn-ghost btn-small"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >Previous</button>
            <button
              className="btn btn-ghost btn-small"
              disabled={offset + limit >= total}
              onClick={() => setOffset(offset + limit)}
            >Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
